/**
 * H12 greybox sect home maps x8 (docs/design/C0_SECTS.md §1): one generator + an 8-entry table. Each map reuses the
 * H7 town shell (townGrey.ts: 96 m flat, safe plaza, monster pocket SE, exit gap in the north wall back to plains)
 * and swaps in a sect hall (a male and a female senior inside) and a training yard. TODO-polish: real layouts.
 */
import type { LayoutSpec } from '../../layout';
import type { DoorLink, InteriorDef } from '../interiors';
import type { SafeZoneDef } from '../safeZones';
import type { ZoneDef } from '../zones';
import { TOWN_PLAZA, townLayout, townZone } from './townGrey';

interface SectRow {
  readonly id: string;
  readonly name: string;
  readonly senior: readonly [string, string];
  readonly tint: readonly [number, number, number];
}

export const SECT_TABLE: readonly SectRow[] = [
  { id: 'bamboo-tide', name: 'สำนักคลื่นไผ่', senior: ['พี่ชายคลื่น', 'พี่สาวคลื่น'], tint: [0.94, 1.04, 1.02] },
  { id: 'mist-peak', name: 'สำนักยอดหมอก', senior: ['พี่ชายหมอก', 'พี่สาวหมอก'], tint: [0.96, 1.0, 1.06] },
  { id: 'cinnabar', name: 'สำนักดาบชาด', senior: ['พี่ชายชาด', 'พี่สาวชาด'], tint: [1.08, 0.96, 0.92] },
  { id: 'pine-ink', name: 'หอหมึกสน', senior: ['พี่ชายหมึก', 'พี่สาวหมึก'], tint: [0.94, 1.0, 0.96] },
  { id: 'bell-stone', name: 'สำนักหินระฆัง', senior: ['พี่ชายระฆัง', 'พี่สาวระฆัง'], tint: [1.04, 1.0, 0.94] },
  { id: 'lantern-road', name: 'ทางโคมเหล็ก', senior: ['พี่ชายตะเกียง', 'พี่สาวตะเกียง'], tint: [1.04, 0.98, 0.9] },
  { id: 'dim-moon', name: 'หุบจันทร์มัว', senior: ['พี่ชายจันทร์', 'พี่สาวจันทร์'], tint: [0.98, 0.94, 1.08] },
  { id: 'snow-pine', name: 'ตำหนักสนหิมะ', senior: ['พี่ชายสนหิมะ', 'พี่สาวสนหิมะ'], tint: [0.96, 1.04, 1.1] },
];

const zid = (id: string): string => `sect-${id}`;

function sectLayout(row: SectRow, seed: number): LayoutSpec {
  const z = zid(row.id);
  const base = townLayout(z, seed);
  return {
    ...base,
    ops: [...base.ops, { name: 'training yard', shape: { rect: [14, 34, 56, 78] }, kind: 'court', level: 0 }],
    // Sect hall in the plaza's north; the training yard is the open court west of the plaza.
    buildings: [
      { ...base.buildings[1]!, id: `${z}-hall`, name: row.name, x0: 41, x1: 55, y0: 30, y1: 40, door: [48, 39, 's'] },
    ],
    points: { ...base.points, yard: [24, 67] },
  } as LayoutSpec;
}

const HALL_PLAN = ['#w######w#', '#........#', '#..1..2..#', '#........#', '#*......*#', '####D#####'];

export const SECT_LAYOUTS: Readonly<Record<string, LayoutSpec>> = Object.fromEntries(
  SECT_TABLE.map((r, i) => [zid(r.id), sectLayout(r, 800 + i)]),
);
export const SECT_ZONES: readonly ZoneDef[] = SECT_TABLE.map((r, i) => townZone(zid(r.id), r.name, 800 + i, r.tint));
export const SECT_SAFE_ZONES: readonly SafeZoneDef[] = SECT_TABLE.map((r) => ({
  zone: zid(r.id) as SafeZoneDef['zone'],
  id: `${zid(r.id)}-plaza`,
  name: `ลาน${r.name}`,
  circle: { at: [TOWN_PLAZA.at[0] + 0.5, TOWN_PLAZA.at[1] + 0.5], r: TOWN_PLAZA.r },
}));
export const SECT_INTERIORS: readonly InteriorDef[] = SECT_TABLE.map((r, i) => ({
  id: `${zid(r.id)}-hall`,
  facing: 's',
  name: r.name,
  zone: zid(r.id) as InteriorDef['zone'],
  template: 'hall',
  seed: 800 + i,
  floors: [
    {
      level: 0,
      name: r.name,
      floor: 'stone',
      wall: 'timber',
      dark: 0.3,
      plan: HALL_PLAN,
      npcs: [
        { spot: '1', name: r.senior[0], title: 'ศิษย์พี่', sprite: 'scholar', lines: ['ยินดีต้อนรับสู่' + r.name + ' {name}'] },
        { spot: '2', name: r.senior[1], title: 'ศิษย์พี่', sprite: 'taoist', lines: ['ฝึกให้มั่นคงก่อน {name} แล้วค่อยก้าวต่อไป'] },
      ],
    },
  ],
}));
export const SECT_DOOR_LINKS: readonly DoorLink[] = SECT_TABLE.map((r) => ({
  zone: zid(r.id) as DoorLink['zone'],
  building: `${zid(r.id)}-hall`,
  interior: `${zid(r.id)}-hall`,
}));
